import { useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Eye, EyeOff, Loader2, Lock, Mail, ShieldAlert } from 'lucide-react';
import { useI18n } from '@/contexts/i18n-hook';

/**
 * O schema recebe o `t` do componente em vez de importar `traduzir`: a mensagem
 * de validação é texto de tela e precisa acompanhar a troca de idioma.
 */
const criarLoginSchema = (t: (chave: string) => string) =>
  z.object({
    email: z
      .string()
      .min(1, t('auth.formulario.validacao.emailObrigatorio'))
      .email(t('auth.formulario.validacao.emailInvalido')),
    senha: z.string().min(1, t('auth.formulario.validacao.senhaObrigatoria')),
  });

type LoginFormData = z.infer<ReturnType<typeof criarLoginSchema>>;

export interface LoginFormProps {
  onSubmit: (data: LoginFormData) => Promise<void> | void;
  error?: string;
}

/**
 * Distingue bloqueio/excesso de tentativas de credencial inválida, para dar ao
 * usuário uma instrução útil ("aguarde N minutos") em vez do erro genérico.
 * A mensagem de credencial inválida continua genérica de propósito: dizer se o
 * e-mail existe entregaria a lista de usuários a quem estiver testando.
 */
function ehErroDeBloqueio(mensagem?: string): boolean {
  if (!mensagem) return false;
  const m = mensagem.toLowerCase();
  return m.includes('bloquead') || m.includes('muitas tentativas') || m.includes('aguarde');
}

export function LoginForm({ onSubmit, error }: LoginFormProps) {
  const { t } = useI18n();
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [capsLockAtivo, setCapsLockAtivo] = useState(false);
  const emailId = useId();
  const senhaId = useId();
  const erroId = useId();

  const loginSchema = useMemo(() => criarLoginSchema(t), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const bloqueio = ehErroDeBloqueio(error);

  const classeCampo = (temErro: boolean) =>
    [
      'w-full rounded-lg border bg-white py-3 pl-10 text-sm text-schmersal-gray-dark',
      // normal-case anula a regra global do jsuites.css, que força placeholder
      // em maiúsculas em todos os inputs do app.
      'placeholder:text-schmersal-gray-medium placeholder:normal-case',
      'transition-colors outline-none',
      'focus:border-schmersal-turquoise focus:ring-2 focus:ring-schmersal-turquoise/30',
      temErro
        ? 'border-destructive focus:border-destructive focus:ring-destructive/25'
        : 'border-schmersal-gray',
    ].join(' ');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      {error && (
        <div
          id={erroId}
          role="alert"
          aria-live="assertive"
          className={[
            'flex items-start gap-3 rounded-lg border p-3.5',
            bloqueio
              ? 'border-schmersal-yellow-dark/40 bg-schmersal-yellow/10'
              : 'border-destructive/30 bg-destructive/5',
          ].join(' ')}
        >
          {bloqueio ? (
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-schmersal-yellow-dark" aria-hidden="true" />
          ) : (
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden="true" />
          )}
          <p className={`text-sm leading-snug ${bloqueio ? 'text-schmersal-gray-dark' : 'text-destructive'}`}>
            {error}
          </p>
        </div>
      )}

      <div>
        <label htmlFor={emailId} className="mb-1.5 block text-sm font-medium text-schmersal-gray-dark">
          {t('auth.formulario.email')}
        </label>
        <div className="relative">
          <Mail
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-schmersal-gray-medium"
            aria-hidden="true"
          />
          <input
            id={emailId}
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            placeholder={t('auth.formulario.emailPlaceholder')}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? `${emailId}-erro` : undefined}
            className={classeCampo(!!errors.email)}
            {...register('email')}
          />
        </div>
        {errors.email?.message && (
          <p id={`${emailId}-erro`} className="mt-1.5 text-xs text-destructive">
            {errors.email.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={senhaId} className="mb-1.5 block text-sm font-medium text-schmersal-gray-dark">
          {t('auth.formulario.senha')}
        </label>
        <div className="relative">
          <Lock
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-schmersal-gray-medium"
            aria-hidden="true"
          />
          <input
            id={senhaId}
            type={mostrarSenha ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder="••••••••"
            aria-invalid={!!errors.senha}
            aria-describedby={errors.senha ? `${senhaId}-erro` : undefined}
            onKeyUp={(e) => setCapsLockAtivo(e.getModifierState?.('CapsLock') ?? false)}
            onBlur={() => setCapsLockAtivo(false)}
            className={`${classeCampo(!!errors.senha)} pr-11`}
            {...register('senha')}
          />
          <button
            type="button"
            onClick={() => setMostrarSenha((v) => !v)}
            aria-label={mostrarSenha ? t('auth.formulario.ocultarSenha') : t('auth.formulario.mostrarSenha')}
            aria-pressed={mostrarSenha}
            tabIndex={-1}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-schmersal-gray-medium transition-colors hover:bg-schmersal-gray-light hover:text-schmersal-gray-dark focus:outline-none focus:ring-2 focus:ring-schmersal-turquoise/40"
          >
            {mostrarSenha ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.senha?.message && (
          <p id={`${senhaId}-erro`} className="mt-1.5 text-xs text-destructive">
            {errors.senha.message}
          </p>
        )}
        {capsLockAtivo && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-schmersal-yellow-dark">
            <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
            {t('auth.formulario.capsLock')}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-schmersal-blue px-4 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-schmersal-blue-light focus:outline-none focus:ring-2 focus:ring-schmersal-turquoise focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {isSubmitting ? t('auth.formulario.entrando') : t('auth.formulario.entrar')}
      </button>

      <p className="pt-1 text-center text-xs text-schmersal-gray-medium">
        {t('auth.formulario.rodape')}
      </p>
    </form>
  );
}
